/* eslint-disable react-hooks/exhaustive-deps */
import { AutocompleteChangeReason, Chip, FilterOptionsState, TextField, Tooltip } from "@mui/material";
import { Autocomplete } from '@mui/material';
import React, { FC, ReactNode, useEffect } from "react";

type InputComboBoxListProperties = {
    id?: string;
    value?: any;
    defaultValue?: any;
    options: any[];
    label?: string;
    textField: string;
    valueField: string;
    autoComplete?: boolean;
    error?: boolean;
    multiple?: boolean;
    disableClearable?: boolean;
    disabled?: boolean;
    limitTags?: number;
    helperText?: ReactNode;
    renderOptions?: (option: any) => string | JSX.Element;
    renderOptionsLabel?: (option: any) => string
    onChange: (value: any, item?: any, reason?: AutocompleteChangeReason) => void
    onInputChange?: (e: any) => void;
    filterOptions?: (opts: any[], state: FilterOptionsState<any>) => any[]
    prefix?: React.ReactNode;
    onBlur?: Function;
    onFocus?: Function;
    noOptionsText?: string;
    searchValue?: string;
    loadingText?: string;
    isLoading?: boolean
    optionsDisabled?: any[];
}

const InputComboBoxList: FC<InputComboBoxListProperties> = ({
    id,
    label,
    value,
    options,
    textField,
    disabled = false,
    autoComplete = false,
    error = false,
    disableClearable = false,
    valueField,
    renderOptions,
    renderOptionsLabel,
    limitTags,
    multiple,
    onChange,
    onInputChange,
    filterOptions,
    helperText,
    prefix,
    onBlur,
    onFocus,
    searchValue,
    noOptionsText,
    loadingText,
    isLoading = false,
    defaultValue,
    optionsDisabled = [],
}) => {
    const [localValue, setLocalValue] = React.useState<any>();
    const [inputValue, setInputValue] = React.useState('');

    useEffect(() => {
        if (!value) {
            setLocalValue(null)
            setInputValue('')
        } else {
            setLocalValue(Array.isArray(value) ? value.map((v) => v[textField]).join(", ") : value);
            setInputValue(value ? renderOptionsLabel ? renderOptionsLabel(value) : value[textField] : "")
        }
    }, [value]);

    return (
        <>
            <Tooltip title={localValue && multiple ? localValue : localValue && renderOptions ? renderOptions(localValue) : ""} >
                <Autocomplete
                    filterOptions={filterOptions}
                    noOptionsText={noOptionsText || undefined}
                    getOptionDisabled={(option)=>{
                        return option.value ? optionsDisabled?.includes(option.value) : false;
                    }}
                    multiple={multiple}
                    defaultValue={defaultValue}
                    fullWidth
                    id={id}
                    loadingText={loadingText}
                    loading={isLoading}
                    value={value}
                    disabled={disabled}
                    disableClearable={disableClearable}
                    options={options}
                    limitTags={limitTags}
                    inputValue={searchValue !== undefined ? value?.[textField] || searchValue : undefined}
                    autoComplete={autoComplete}
                    getOptionLabel={(option) => {
                        return option ? renderOptionsLabel ? renderOptionsLabel(option) : option[textField] : ""
                    }}
                    isOptionEqualToValue={(option, value) => value ? option[valueField] === value[valueField] : false}
                    onChange={(_event, newValue) => {
                        if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
                            setLocalValue(null);
                            setInputValue('');
                        }

                        if (multiple) {
                            onChange(newValue, -1);
                        } else {
                            const index =
                                newValue && newValue[valueField]
                                ? options.findIndex((o) => o[valueField] === newValue[valueField])
                                : -1;

                            onChange(newValue, index);
                        }
                    }}
                    renderOption={(props, option) => (
                        <li {...props}>
                            {renderOptions
                                ? renderOptions(option)
                                : option[textField]}
                        </li>
                    )}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                            <Chip
                                style={{ margin: "0px 1px 0px 1px" }}
                                size="small"

                                label={option[textField]}
                                {...getTagProps({ index })}
                            />
                        ))
                    }
                    renderInput={(params) => <TextField
                        {...params}
                        InputLabelProps={{
                            shrink: true
                        }}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: params.InputProps.startAdornment || prefix || null,
                            onChange: (event) => {
                                if (typeof (params.inputProps as any).onChange === 'function') {
                                    (params.inputProps as any).onChange(event);
                                }
                                if (typeof onInputChange === 'function') onInputChange(event)
                            }
                        }}
                        label={label}
                        variant="outlined"
                        size="small"
                        margin="normal"
                        fullWidth
                        value={inputValue}
                        helperText={helperText}
                        error={error}
                    />
                    }
                    onFocus={(event: any) => {
                        if (onFocus) {
                            onFocus(event)
                        }
                    }}
                    onBlur={(event: any) => {
                        if (onBlur) {
                            onBlur(event)
                        }
                    }}
                />
            </Tooltip>
        </>
    );
}

export { InputComboBoxList }